import { z } from 'zod';
import {
  policyMatchSchema,
  reviewedTaskSchema,
  triageProposalSchema,
} from '../autopilot/email-triage.ts';
import { actorSchema, isoDateTimeSchema } from './common.ts';

/** Service inbox: mail synced from the env's Microsoft 365 service mailbox. */

export const inboxStatusSchema = z.enum(['open', 'replied', 'closed']);
export type InboxStatus = z.infer<typeof inboxStatusSchema>;

export const mailFolderSchema = z.enum(['inbox', 'sentitems']);
export type MailFolder = z.infer<typeof mailFolderSchema>;

export const mailPersonSchema = z.object({ name: z.string(), email: z.string() });
export type MailPerson = z.infer<typeof mailPersonSchema>;

export const inboxAttachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  contentType: z.string(),
  /** Bytes. */
  size: z.number().int().nonnegative(),
  /** Embedded in the body (a `cid:` image) rather than attached. */
  isInline: z.boolean(),
  /** MIME Content-ID the HTML body references as `cid:<contentId>`. */
  contentId: z.string().nullable(),
  /** Too large to download through ops-desk; open the message in Outlook. */
  tooLarge: z.boolean(),
});
export type InboxAttachment = z.infer<typeof inboxAttachmentSchema>;

export const inboxMessageSchema = z.object({
  id: z.string(),
  /** `inbox` = received by the mailbox, `sentitems` = sent from it. */
  folder: mailFolderSchema,
  subject: z.string(),
  from: mailPersonSchema.nullable(),
  to: z.array(mailPersonSchema),
  cc: z.array(mailPersonSchema),
  /** Received time for inbox mail, sent time for sent items. */
  at: isoDateTimeSchema,
  /** HTML or plain text as sent. HTML must be rendered sandboxed. */
  body: z.object({ contentType: z.enum(['html', 'text']), content: z.string() }),
  /** The stored body was cut; open `webLink` for the full message. */
  bodyTruncated: z.boolean(),
  preview: z.string(),
  attachments: z.array(inboxAttachmentSchema),
  /** Opens the message in Outlook on the web. */
  webLink: z.string().nullable(),
});
export type InboxMessage = z.infer<typeof inboxMessageSchema>;

export const inboxConversationSummarySchema = z.object({
  id: z.string(),
  mailbox: z.string(),
  subject: z.string(),
  status: inboxStatusSchema,
  /** Why it is closed: an operator's reason or the automatic rule. */
  closedReason: z.string().nullable(),
  closedBy: actorSchema.nullable(),
  closedAt: isoDateTimeSchema.nullable(),
  isRead: z.boolean(),
  messageCount: z.number().int(),
  latestAt: isoDateTimeSchema,
  /** Sender of the newest inbound message. */
  latestFrom: mailPersonSchema.nullable(),
  preview: z.string(),
  hasAttachments: z.boolean(),
  /** Oldest inbound message still waiting for a reply (SLA start). */
  waitingSince: isoDateTimeSchema.nullable(),
});
export type InboxConversationSummary = z.infer<typeof inboxConversationSummarySchema>;

export const inboxConversationSchema = inboxConversationSummarySchema.extend({
  /** Oldest first; inbound and sent on one timeline. */
  messages: z.array(inboxMessageSchema),
});
export type InboxConversation = z.infer<typeof inboxConversationSchema>;

const booleanQuery = z.enum(['true', 'false']).transform((v) => v === 'true');

export const INBOX_PAGE_SIZES = [10, 25, 50] as const;
export const DEFAULT_INBOX_PAGE_SIZE = 25;

export const listInboxQuerySchema = z.strictObject({
  status: inboxStatusSchema.optional(),
  unread: booleanQuery.optional(),
  /** Subject, sender name or any participant address (case-insensitive). */
  q: z.string().trim().min(1).max(200).optional(),
  /** 1-based; a page past the end returns no items (the total still counts every match). */
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  size: z.coerce
    .number()
    .int()
    .refine((n) => (INBOX_PAGE_SIZES as readonly number[]).includes(n))
    .default(DEFAULT_INBOX_PAGE_SIZE),
});
export type ListInboxQuery = z.infer<typeof listInboxQuerySchema>;

/** One page of conversations, newest activity first. */
export const inboxPageSchema = z.object({
  items: z.array(inboxConversationSummarySchema),
  /** Conversations matching the filters. */
  total: z.number().int().nonnegative(),
  page: z.number().int(),
  size: z.number().int(),
});
export type InboxPage = z.infer<typeof inboxPageSchema>;

export const closeConversationRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(500).optional(),
});
export type CloseConversationRequest = z.infer<typeof closeConversationRequestSchema>;

export const replyRequestSchema = z.strictObject({
  /** Plain text; Graph places it above the quoted message. */
  body: z.string().trim().min(1).max(20_000),
  replyAll: z.boolean().default(false),
});
export type ReplyRequest = z.infer<typeof replyRequestSchema>;

/** The updated summary and the sent message (not the whole thread: replies are stored for replay). */
export const replyResponseSchema = z.object({
  conversation: inboxConversationSummarySchema,
  message: inboxMessageSchema,
});
export type ReplyResponse = z.infer<typeof replyResponseSchema>;

// --- triage ----------------------------------------------------------------------------------

/**
 * Where a conversation's triage stands, for its newest received message.
 * - `off`: triage is switched off in this environment.
 * - `not_started`: nothing has started triage for that message yet.
 * - `running`: finding the policy and proposing tasks.
 * - `awaiting_review`: waiting for an operator to approve or dismiss.
 * - `no_suggestions`: finished with nothing to do (e.g. a thank-you note).
 * - `approved` / `dismissed` / `timed_out`: the review outcome.
 * - `failed`: the run failed; `failure` says why.
 */
export const inboxTriageStatusSchema = z.enum([
  'off',
  'not_started',
  'running',
  'awaiting_review',
  'no_suggestions',
  /** No policy number in the email: no diary task can be created, so no run is started. */
  'manual_triage',
  'approved',
  'dismissed',
  'timed_out',
  'failed',
]);
export type InboxTriageStatus = z.infer<typeof inboxTriageStatusSchema>;

export const inboxTriageSchema = z.object({
  /** `mock`: sample results generated by ops-desk, nothing reaches autopilot. */
  mode: z.enum(['off', 'mock', 'temporal']),
  status: inboxTriageStatusSchema,
  /** The received message triaged; null when the conversation has none. */
  messageId: z.string().nullable(),
  startedAt: isoDateTimeSchema.nullable(),
  policy: policyMatchSchema.nullable(),
  proposal: triageProposalSchema.nullable(),
  /** Set once an operator reviewed (or the review timed out). */
  review: z
    .object({
      decision: z.enum(['approve', 'dismiss', 'timed_out', 'invalid']),
      /** False while autopilot's diary creation from email is switched off. */
      createEnabled: z.boolean(),
      /** Approved codes autopilot will (or would) post. */
      planned: z.array(z.object({ code: z.string(), status: z.string() })),
      unsupported: z.array(z.object({ code: z.string(), reason: z.string() })),
    })
    .nullable(),
  failure: z.string().nullable(),
});
export type InboxTriage = z.infer<typeof inboxTriageSchema>;

export const triageReviewRequestSchema = z
  .strictObject({
    /** The message the proposal was for; a newer message makes the review stale (409). */
    messageId: z.string().min(1),
    decision: z.enum(['approve', 'dismiss']),
    tasks: z.array(reviewedTaskSchema).max(20).default([]),
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .refine((r) => r.decision === 'dismiss' || r.tasks.length > 0, {
    message: 'Approve at least one task, or dismiss',
    path: ['tasks'],
  });
export type TriageReviewRequest = z.infer<typeof triageReviewRequestSchema>;
